import { useMemo } from "react";
import { diffChars } from "@/lib/text-diff";
import { cn } from "@/lib/utils";

interface DiffTextProps {
	oldText: string;
	newText: string;
	className?: string;
}

/**
 * Hiển thị `oldText` → `newText` dưới dạng MỘT chuỗi hợp nhất kiểu git: phần bị
 * bỏ nằm ngay cạnh phần thêm vào, phần chung giữ nguyên. Diff ở cấp ký tự, nên
 * một chữ số sai giữa chuỗi điểm dài vẫn hiện ra thay vì chìm vào cả ô.
 *
 * Chuỗi hợp nhất chỉ đọc được bằng MẮT: đọc tuần tự "8" gạch bỏ rồi "9" ra
 * thành "89.50". Nên phần trực quan bị `aria-hidden`, và bản đọc được cho trình
 * đọc màn hình nằm ở `sr-only` phía trước. Vì lý do đó component này KHÔNG được
 * đặt bên trong một `<Button>`: nhãn nút sẽ thành chuỗi hợp nhất, tức tên của
 * một trường không tồn tại.
 */
export function DiffText({ oldText, newText, className }: DiffTextProps) {
	const segments = useMemo(() => diffChars(oldText, newText), [oldText, newText]);

	return (
		<span className={cn("break-words", className)}>
			<span className="sr-only">{`đổi từ "${oldText}" thành "${newText}"`}</span>
			<span aria-hidden="true" title={`"${oldText}" → "${newText}"`}>
				{segments.map((segment, index) => {
					if (segment.type === "equal") return <span key={index}>{segment.text}</span>;
					if (segment.type === "remove") {
						return (
							<del
								key={index}
								className="rounded-[2px] bg-red-100 px-0.5 text-red-700 decoration-red-400 dark:bg-red-950 dark:text-red-400"
							>
								{segment.text}
							</del>
						);
					}
					return (
						<ins
							key={index}
							className="rounded-[2px] bg-green-100 px-0.5 font-medium text-green-800 no-underline dark:bg-green-950 dark:text-green-300"
						>
							{segment.text}
						</ins>
					);
				})}
			</span>
		</span>
	);
}
